<template>
  <div>
    <label
      class="block mb-2 text-sm font-medium text-gray-900 dark:text-white sr-only"
      >{{ label }}</label
    >
    <button
      :id="itemKey('button')"
      :data-dropdown-toggle="itemKey('dropdown')"
      class="flex justify-between items-center bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full px-2.5 py-2.5 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 xl:min-w-[160px] 2xl:min-w-[180px]"
    >
      <span class="flex-1 text-left truncate flex items-center gap-1">
        <template v-if="selectedItems.length > 1">
          <img 
            v-for="(item, index) in selectedItems.slice(0, 5)" 
            :key="item.value"
            :src="getFeatureIconUrl(item.value)"
            :alt="item.label"
            class="w-4 h-4 dark:invert"
            @error="handleImageError"
          />
          <span v-if="selectedItems.length > 5" class="text-xs text-gray-500 dark:text-gray-400">+{{ selectedItems.length - 5 }}</span>
        </template>
        <template v-else>
          {{ displayLabel }}
        </template>
      </span>
      <svg
        class="w-4 h-4 ms-2"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M19 9l-7 7-7-7"
        />
      </svg>
    </button>

    <div
      :id="itemKey('dropdown')"
      class="z-20 hidden bg-white rounded-lg shadow w-60 dark:bg-gray-700"
    >
      <ais-refinement-list
        :attribute="attribute"
        :transform-items="transformItems"
        operator="and"
        searchable
        :show-more="false"
        :limit="1000"
      >
        <template
          v-slot="{
            items,
            isShowingMore,
            isFromSearch,
            canToggleShowMore,
            refine,
            createURL,
            toggleShowMore,
            searchForItems,
            sendEvent,
          }"
        >
          <div class="p-3">
            <label for="input-group-search" class="sr-only">{{$t('actions.search')}}</label>
            <div class="relative">
              <div
                class="absolute inset-y-0 rtl:inset-r-0 start-0 flex items-center ps-3 pointer-events-none"
              >
                <svg
                  class="w-4 h-4 text-gray-500 dark:text-gray-400"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 20 20"
                >
                  <path
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"
                  />
                </svg>
              </div>
              <input
                type="text"
                id="input-group-search"
                class="block w-full p-2 ps-10 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-600 dark:border-gray-500 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                @input="search($event.currentTarget.value)"
                :placeholder="$t('actions.search')"
              />
            </div>
          </div>

          <ul
            class="h-48 px-3 pb-3 overflow-y-auto text-sm text-gray-700 dark:text-gray-200"
            :aria-labelledby="itemKey('button')"
          >
            <li v-if="isFromSearch && !items.length">No results.</li>
            <li v-for="item in getFilteredItems(items)" :key="item.value">
              <div
                class="flex items-center ps-2 rounded hover:bg-gray-100 dark:hover:bg-gray-600"
              >
                <input
                  :checked="item.isRefined"
                  @change="refine(item.value); track('search_filter', { filter: 'feature', value: item.value })"
                  :id="itemKey('inputs', item.value)"
                  type="checkbox"
                  class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-700 dark:focus:ring-offset-gray-700 focus:ring-2 dark:bg-gray-600 dark:border-gray-500"
                />
                <label
                  :for="itemKey('inputs', item.value)"
                  class="w-full py-2 ms-2 text-sm font-medium text-gray-900 rounded dark:text-gray-300 flex items-center gap-2"
                >
                  <img 
                    :src="getFeatureIconUrl(item.value)"
                    :alt="item.label"
                    class="w-5 h-5 dark:invert"
                    @error="handleImageError"
                  />
                  <span>{{ item.label }} ({{ item.count.toLocaleString() }})</span>
                </label>
              </div>
            </li>
          </ul>
        </template>
      </ais-refinement-list>
    </div>
  </div>
</template>
<script setup lang="ts">
import { useI18n } from 'vue-i18n';
const { t } = useI18n();
const { track } = useAnalytics();
const { $thumbnailGenerator } = useNuxtApp();
const query = ref(null);
const selectedItems = ref([]);

import {
  AisInstantSearch,
  AisNumericMenu,
  AisSearchBox,
  AisHits,
  AisMenuSelect,
  AisConfigure,
  AisPagination,
  AisStats,
  AisRefinementList,
  AisHighlight,
} from "vue-instantsearch/vue3/es";
import { initDropdowns } from "flowbite";

const props = defineProps({
  label: {
    label: String,
    required: true,
  },
  attribute: {
    label: String,
    required: true,
  },
});

const displayLabel = computed(() => {
  const count = selectedItems.value.length;
  
  if (count === 0) {
    return props.label;
  }
  
  if (count === 1) {
    return selectedItems.value[0].label;
  }
  
  // Multiple items: show first item + count of additional items
  return `${selectedItems.value[0].label}, +${count - 1}`;
});

function transformItems(items) {
  return items.map(item => ({
    ...item,
    label: t('features.' + item.value),
  }));
}

function search(text) {
  query.value = text;
}

function getFilteredItems(items) {
  // Update selected items
  selectedItems.value = items.filter(item => item.isRefined);
  
  const filtered = items.filter(item => {
    // Always show selected items regardless of search query
    if (item.isRefined) return true;
    // For non-selected items, filter based on search query
    return query.value ? item.label.toLowerCase().includes(query.value.toLowerCase()) : true;
  });
  
  // Sort to show selected items first
  return filtered.sort((a, b) => {
    if (a.isRefined && !b.isRefined) return -1;
    if (!a.isRefined && b.isRefined) return 1;
    return 0;
  });
}

function itemKey(prefix, id = null) {
  return (
    props.attribute.replaceAll(".", "-") +
    "-" +
    prefix +
    "-item" +
    (id ? "-" + id : "")
  );
}

function getFeatureIconUrl(featureSlug) {
  return $thumbnailGenerator.featureIcon(featureSlug);
}

function handleImageError(event) {
  // Hide broken images
  event.target.style.display = 'none';
}
onMounted(() => {
  initDropdowns();
});
</script>
